import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, AlertIcon, Box, Button, FormControl, FormLabel, Heading, HStack, Icon,
  Image, Input, InputGroup, InputRightElement, Stack, Text,
} from '@chakra-ui/react';
import { useResetPasswordMutation } from '../redux/services/auth';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Local validation — fail fast so we don't ship a 400 round-trip for
  // the obvious cases. The backend has its own policy (length,
  // complexity) and will reject anything we let through if too weak.
  const minLength = password.length >= 8;
  const matches = password === confirm && password.length > 0;
  const canSubmit = !!token && minLength && matches && !isLoading;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('This reset link is missing or malformed. Request a new one from the Forgot password page.');
      return;
    }
    if (!minLength) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!matches) {
      setError('The two passwords don\'t match.');
      return;
    }
    try {
      await resetPassword({ password, token }).unwrap();
      setDone(true);
      // Give the success alert a moment to be visible, then push to login
      setTimeout(() => navigate('/login'), 1800);
    } catch (err: any) {
      // The token has a 15 min TTL on the backend. Give the user an
      // actionable hint for the common failure modes.
      const msg = err?.data?.message || '';
      if (/expired|invalid.*token|token.*expired|token.*invalid/i.test(msg)) {
        setError('This reset link has expired or already been used. Request a new one.');
      } else {
        setError(msg || 'Could not reset your password. Try again, or request a new link.');
      }
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="stretch"
      justifyContent="center"
      bgGradient="linear(180deg, #0F0B1F 0%, #1A0F2E 40%, #2E1240 100%)"
      color="whiteAlpha.900"
      px={{ base: 5, md: 8 }}
      py={{ base: 8, md: 12 }}
      position="relative"
      overflow="hidden"
    >
      {/* Mirrors LoginPage / ForgotPasswordPage so the whole auth surface
          looks like one world. */}
      <Box
        position="absolute" top="-140px" left="50%" transform="translateX(-50%)"
        w="520px" h="520px"
        bg="radial-gradient(circle, rgba(233,74,143,0.32) 0%, rgba(233,74,143,0) 65%)"
        pointerEvents="none" aria-hidden
      />
      <Box
        position="absolute" bottom="-180px" right="-140px"
        w="480px" h="480px"
        bg="radial-gradient(circle, rgba(115,84,255,0.28) 0%, rgba(115,84,255,0) 65%)"
        pointerEvents="none" aria-hidden
      />

      <Stack w="full" maxW="440px" spacing={7} justify="center" position="relative" zIndex={1}>
        <Stack spacing={4} align="center" textAlign="center">
          <Image src="/logo/logo_single.png" alt="MixGenie" boxSize="64px" borderRadius="14px" />
          <Heading
            as="h1"
            fontSize={{ base: '28px', md: '32px' }}
            fontWeight={800}
            lineHeight="1.1"
            letterSpacing="-0.01em"
            style={{ textWrap: 'balance' as any }}
          >
            Set a new password
          </Heading>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="whiteAlpha.700" maxW="360px">
            Pick a new password for your MixGenie account. The link in your email expires 15 minutes after it was sent.
          </Text>
        </Stack>

        <Stack
          spacing={4}
          bg="rgba(20, 14, 36, 0.7)"
          backdropFilter="blur(18px)"
          border="1px solid"
          borderColor="whiteAlpha.200"
          borderRadius="20px"
          p={{ base: 6, md: 7 }}
          boxShadow="0 24px 60px rgba(0,0,0,0.45)"
        >
          {done ? (
            <Stack spacing={3}>
              <Alert
                status="success"
                borderRadius="12px"
                bg="rgba(72,187,120,0.15)"
                color="whiteAlpha.900"
                border="1px solid"
                borderColor="rgba(72,187,120,0.4)"
              >
                <AlertIcon color="green.300" />
                <Box>
                  <Text fontWeight={700} mb={0.5}>Password updated</Text>
                  <Text fontSize="sm" color="whiteAlpha.800">Signing you in… you'll land on the login page in a second.</Text>
                </Box>
              </Alert>
            </Stack>
          ) : !token ? (
            // Guard: a user who lands on /reset-password with no token
            // probably clicked an old or malformed link. Point them back
            // at the forgot-password page instead of showing a form that
            // will fail on submit.
            <Alert status="warning" borderRadius="12px" bg="rgba(201, 61, 95, 0.18)" color="whiteAlpha.900" border="1px solid" borderColor="rgba(233, 74, 143, 0.55)">
              <AlertIcon color="pink.300" />
              <Box>
                <Text fontWeight={700} mb={0.5}>Link missing a token</Text>
                <Text fontSize="sm" color="whiteAlpha.800">
                  This reset link looks incomplete. <Link to="/forgot-password" style={{ color: '#f0abfc', textDecoration: 'underline' }}>Request a new one</Link>.
                </Text>
              </Box>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {error && (
                  <Alert status="error" borderRadius="10px" bg="red.900" color="white" border="1px solid" borderColor="red.600">
                    <AlertIcon color="red.200" />
                    {error}
                  </Alert>
                )}

                <FormControl isRequired>
                  <FormLabel fontSize="xs" letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.700" mb={1}>
                    New password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={show ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor="whiteAlpha.200"
                      color="whiteAlpha.900"
                      _hover={{ borderColor: 'whiteAlpha.400' }}
                      _focus={{ borderColor: 'pink.300', bg: 'whiteAlpha.200', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
                      _placeholder={{ color: 'whiteAlpha.400' }}
                      borderRadius="12px"
                      size="lg"
                      fontSize="md"
                      autoComplete="new-password"
                      autoFocus
                    />
                    <InputRightElement h="100%">
                      <Button
                        variant="ghost"
                        size="sm"
                        mr={1}
                        color="whiteAlpha.700"
                        _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                        onClick={() => setShow((v) => !v)}
                        tabIndex={-1}
                      >
                        {show ? 'Hide' : 'Show'}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="xs" letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.700" mb={1}>
                    Confirm password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Type it again"
                      bg="whiteAlpha.100"
                      border="1px solid"
                      borderColor={confirm.length > 0 && !matches ? 'red.400' : 'whiteAlpha.200'}
                      color="whiteAlpha.900"
                      _hover={{ borderColor: 'whiteAlpha.400' }}
                      _focus={{ borderColor: 'pink.300', bg: 'whiteAlpha.200', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
                      _placeholder={{ color: 'whiteAlpha.400' }}
                      borderRadius="12px"
                      size="lg"
                      fontSize="md"
                      autoComplete="new-password"
                    />
                    <InputRightElement h="100%">
                      <Button
                        variant="ghost"
                        size="sm"
                        mr={1}
                        color="whiteAlpha.700"
                        _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
                        onClick={() => setShowConfirm((v) => !v)}
                        tabIndex={-1}
                      >
                        {showConfirm ? 'Hide' : 'Show'}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  {confirm.length > 0 && !matches && (
                    <Text fontSize="xs" color="red.300" mt={1}>Passwords don't match.</Text>
                  )}
                </FormControl>

                <Button
                  type="submit"
                  isLoading={isLoading}
                  isDisabled={!canSubmit}
                  size="lg"
                  w="full"
                  bgGradient="linear(90deg, #ec4899 0%, #a855f7 100%)"
                  color="white"
                  fontWeight={700}
                  _hover={{ bgGradient: 'linear(90deg, #f472b6 0%, #c084fc 100%)' }}
                  _active={{ opacity: 0.9 }}
                  _disabled={{ opacity: 0.45, cursor: 'not-allowed' }}
                  borderRadius="12px"
                  data-cta="auth:reset-submit"
                >
                  Set new password
                </Button>
              </Stack>
            </form>
          )}

          <Text fontSize="sm" textAlign="center" color="whiteAlpha.700" mt={2}>
            <Link to="/login" style={{ color: '#f0abfc' }}>
              ← Back to sign in
            </Link>
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
}
